import React, { useRef } from 'react';
import { Paperclip, SendHorizontal } from 'lucide-react';
import { cn } from '../../lib/utils';
import VoiceRecorderButton from './VoiceRecorderButton';

export default function ChatInput({
  value,
  onChange,
  onSend,
  onAttach,
  onVoiceRecorded,
  placeholder = 'Type a message',
  disabled = false,
  sending = false,
  uploadPct = null,
  onTyping,
  inputRef: externalRef,
}) {
  const internalRef = useRef(null);
  const inputRef = externalRef || internalRef;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend?.();
      // Reset height and keep focus after send
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      requestAnimationFrame(() => inputRef.current?.focus?.());
    }
  };

  const handleInput = (e) => {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  return (
    <div className="p-3 bg-card border-t border-border shrink-0">
      {uploadPct != null && (
        <div className="mb-2 text-xs text-muted-foreground flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-black/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${uploadPct}%` }}
            />
          </div>
          <span>Uploading… {Math.round(uploadPct)}%</span>
        </div>
      )}
      <div className="flex items-end gap-2">
        {onAttach && (
          <label
            className="w-10 h-10 rounded-full flex items-center justify-center border border-border hover:bg-muted transition cursor-pointer shrink-0"
            title="Attach file"
          >
            <Paperclip className="w-5 h-5 text-muted-foreground" />
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onAttach(f);
                e.target.value = '';
              }}
            />
          </label>
        )}

        {onVoiceRecorded && <VoiceRecorderButton onRecorded={onVoiceRecorded} />}

        <textarea
          ref={inputRef}
          rows={1}
          className={cn(
            'form-input flex-1 min-w-0 rounded-2xl px-4 py-2.5 text-sm resize-none overflow-hidden',
            'border-border focus:ring-2 focus:ring-primary/20'
          )}
          style={{ minHeight: '42px', maxHeight: '120px' }}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            onChange?.(e.target.value);
            onTyping?.();
          }}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />

        <button
          type="button"
          onClick={() => {
            onSend?.();
            requestAnimationFrame(() => inputRef.current?.focus?.());
          }}
          disabled={disabled}
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition',
            'bg-primary text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          title="Send"
        >
          <SendHorizontal className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
